import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useState, useEffect } from "react";
import moment from "moment";
import Select from "react-select";
import JackpotXWinnerServices from "../../../services/JackpotXWinnerServices";

// Pattern: one or more segments of digits + "x", separated by "/" (e.g. 20x, 20x/30x, 20x/30x/40x)
const WINNER_X_PATTERN = /^\d+x(\/\d+x)*$/;

const validationSchema = Yup.object().shape({
  date_filter: Yup.string().required("Date filter is required"),
  session_id: Yup.string().required("Please select a session"),
  winner_x: Yup.string()
    .required("Winner X is required")
    .matches(WINNER_X_PATTERN, "Invalid format. Use format like: 20x/30x/40x"),
  winner_yantra: Yup.number()
    .typeError("Winner yantra is required")
    .integer("Use a whole number")
    .min(1, "Yantra must be at least 1")
    .required("Winner yantra is required"),
});

const CreateJackpotXWinnerModal = (props) => {
  const { show, setShow, activeGame, getJackpotXWinnerData, yantraData } = props;

  const jackpotXWinnerApi = new JackpotXWinnerServices();
  const [isLoading, setIsLoading] = useState(false);
  const [sessionList, setSessionList] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const dateOptionsList = [
    { value: "today", label: "Today" },
    { value: "tomorrow", label: "Tomorrow" },
  ];

  const initialValues = {
    date_filter: "today",
    winner_x: "",
    session_id: "",
    winner_yantra: "",
  };
  const onClose = () => {
    setShow(false);
  };

  const getSessionList = async (date_filter) => {
    try {
      const response = await jackpotXWinnerApi.getSessionList(
        activeGame,
        date_filter,
      );
      if (response.isSuccess) {
        const dataList = response.data;
        const optionList = dataList.map((item) => ({
          id: item.id,
          value: item.session_no,
          label: `${moment(item.start_time).format("hh:mm A")} - ${moment(item.end_time).format("hh:mm A")}`,
        }));
        setSessionList(optionList);
      } else {
        setSessionList([]);
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error in getSessionList:", error);
      setSessionList([]);
      toast.error("Error in get session list");
    }
  };

  useEffect(() => {
    if (show) {
      getSessionList(initialValues.date_filter);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="md"
    >
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">
          Add Jackpot Winner [
          {activeGame === "3min" ? "3 Min Session" : "5 Min Session"}]
        </h5>
        <button className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setIsLoading(true);
          setSubmitting(true);
          const payload = {
            game: activeGame,
            date_filter: values.date_filter,
            session_id: values.session_id,
            winner_x: values.winner_x,
            yantra_id: Number(values.winner_yantra),
          };
          jackpotXWinnerApi
            .createJackpotXWinner(payload)
            .then((response) => {
              if (response.isSuccess) {
                toast.success(response.message);
                onClose();
                getJackpotXWinnerData();
              } else {
                toast.error(response.message);
              }
            })
            .catch((error) => {
              console.error("Error in createJackpotXWinner:", error);
              toast.dismiss();
              toast.error(
                error?.response?.data?.message ||
                  "Error in create jackpot winner",
              );
            })
            .finally(() => {
              setIsLoading(false);
              setSubmitting(false);
            });
        }}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }) => (
          <Form className="addAdmin-form">
            <Modal.Body className="modal-body-scroll">
              <div className="form-group mb-2">
                <label htmlFor="date_filter" className="form-label mb-0">
                  Date Filter <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <Select
                    className="mt-1 w-100"
                    options={dateOptionsList}
                    value={dateOptionsList.find(
                      (option) => option.value === values.date_filter,
                    )}
                    id="date_filter"
                    name="date_filter"
                    onChange={(selectedItems) => {
                      setFieldValue("date_filter", selectedItems.value);
                      getSessionList(selectedItems.value);
                    }}
                  />
                </div>
                <ErrorMessage name="date_filter">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>
              <div className="form-group mb-2">
                <label htmlFor="session_id" className="form-label mb-0">
                  Select Session <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <Select
                    className="mt-1 w-100"
                    options={sessionList}
                    value={sessionList.find(
                      (option) => option.id === values.session_id,
                    )}
                    id="session_id"
                    name="session_id"
                    onChange={(selectedItems) => {
                      setFieldValue("session_id", selectedItems.id);
                      setSelectedSession(selectedItems);
                    }}
                  />
                </div>
                <ErrorMessage name="session_id">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="form-group mb-2">
                <label htmlFor="winner_yantra" className="form-label mb-0">
                  SELECT YANTRA (ONE){" "}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <div
                    className="d-flex flex-wrap gap-2"
                    role="group"
                    aria-label="Select winner yantra slot"
                  >
                    {(Array.isArray(yantraData) ? yantraData : []).map(
                      (y, idx) => {
                        const selected = Number(values.winner_yantra) === y.id;
                        const name =
                          (y?.yantra_name && String(y.yantra_name).trim()) ||
                          `Yantra ${y?.id ?? idx + 1}`;
                        const tip = `${name}`;
                        return (
                          <button
                            key={y?.id ?? idx}
                            type="button"
                            className={`btn btn-sm ${
                              selected ? "btn-primary" : "btn-outline-secondary"
                            }`}
                            style={{ minWidth: "2.25rem" }}
                            title={tip}
                            aria-label={`Position ${idx + 1}: ${tip}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFieldValue("winner_yantra", y.id);
                            }}
                          >
                            {idx + 1}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
                <ErrorMessage name="winner_yantra">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="form-group mb-2">
                <label htmlFor="winner_x" className="form-label mb-0">
                  Winner X <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <Field
                    type="text"
                    id="winner_x"
                    name="winner_x"
                    placeholder="Enter winner ex:2x/3x/4x"
                    className={`form-input ${errors.winner_x && touched.winner_x ? "input-error" : ""}`}
                  />
                </div>
                <ErrorMessage name="winner_x">
                  {(msg) => <div className="admin_error-text">{msg}</div>}
                </ErrorMessage>
              </div>
              {/* Submit Button */}
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="submit"
                  className="add_admin-btn"
                  disabled={isSubmitting || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    "Add Now"
                  )}
                </button>
              </div>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateJackpotXWinnerModal;
